import { forwardRef, memo, PropsWithChildren, useMemo, useState } from 'react';
import { AvatarProps } from './avatar.types';
import { getColorFromString } from './avatar.utils';
import { getAvatarSettings } from './avatar.constants';
import { SVG } from '../../svg';
import { Text } from '../text';
import { AvatarGroup } from './avatar-group';
import { StyledAvatar } from './avatar.styled';
import { Icons } from 'assets';

const AvatarComponent = memo(
  forwardRef<HTMLDivElement, PropsWithChildren<AvatarProps>>(
    (
      {
        size = 'm',
        color = 'primary',
        colorKey,
        src,
        srcSet,
        icon,
        onError,
        children,
        ...restProps
      },
      forwardedRef
    ) => {
      const [status, setStatus] = useState<'loading' | 'fulfilled' | 'error'>(
        'loading'
      );
      const { iconSize, textMode } = getAvatarSettings()[size];
      const hasChildren = !!(children || icon || src || srcSet);

      const resolvedColor = useMemo(() => {
        return colorKey ? getColorFromString(colorKey) : color;
      }, [color, colorKey]);

      const handleError = () => {
        const errorFlag = onError ? onError() : undefined;

        if (errorFlag !== false) {
          setStatus('error');
        }
      };

      const defaultProps = {
        ...restProps,
        ref: forwardedRef,
        $size: size,
        $color: resolvedColor,
        $hasChildren: hasChildren,
      };

      if (src) {
        return (
          <StyledAvatar
            {...defaultProps}
            $color={status !== 'fulfilled' ? resolvedColor : color}
          >
            {status !== 'fulfilled' &&
              (children ? (
                <Text variant={textMode}>{children}</Text>
              ) : (
                <SVG src={Icons.User} props={{ size: `${iconSize}px` }} />
              ))}
            <img
              src={src}
              srcSet={srcSet}
              alt={'Аватарка пользователя'}
              style={{ display: status !== 'fulfilled' ? 'none' : 'block' }}
              onLoad={() => setStatus('fulfilled')}
              onError={handleError}
            />
          </StyledAvatar>
        );
      }

      if (icon) {
        return (
          <StyledAvatar {...defaultProps}>
            <SVG src={Icons[icon]} props={{ size: `${iconSize}px` }} />
          </StyledAvatar>
        );
      }

      if (children) {
        return (
          <StyledAvatar {...defaultProps}>
            <Text variant={textMode}>{children}</Text>
          </StyledAvatar>
        );
      }

      return (
        <StyledAvatar {...defaultProps} $color="gray">
          <SVG src={Icons.ImageSquare} props={{ size: `${iconSize}px` }} />
        </StyledAvatar>
      );
    }
  )
);

export const Avatar = Object.assign(AvatarComponent, {
  Group: AvatarGroup,
});
